import { useState } from "react";
import { suspiciousContracts } from "../../../mocks/legal";

export default function ToxicClauseDetector() {
  const [selectedContract, setSelectedContract] = useState(suspiciousContracts[0]);
  const [analysisState, setAnalysisState] = useState<"idle" | "analyzing" | "done">("idle");
  const [replyMode, setReplyMode] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyDone, setReplyDone] = useState(false);

  const handleAnalyze = () => {
    setAnalysisState("analyzing");
    setTimeout(() => setAnalysisState("done"), 2000);
  };

  const handleReply = () => {
    if (!replyText.trim()) return;
    setReplyDone(true);
  };

  const getRiskStyle = (severity: string) => {
    if (severity === "위험") return "bg-[#E8375F]/10 text-[#E8375F] border-[#E8375F]/30";
    return "bg-[#FF9F43]/10 text-[#FF9F43] border-[#FF9F43]/30";
  };

  return (
    <div className="space-y-4">
      {/* Contract Selector */}
      <div className="flex items-center gap-3">
        {suspiciousContracts.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => { setSelectedContract(c); setAnalysisState("idle"); setReplyMode(false); setReplyDone(false); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm transition-all cursor-pointer whitespace-nowrap ${
              selectedContract.id === c.id
                ? c.riskLevel === "위험" ? "border-[#E8375F] bg-[#E8375F]/10 text-[#E8375F]" : "border-[#00C896] bg-[#00C896]/10 text-[#00C896]"
                : "border-[#2A2D3E] text-[#8B91A8] hover:border-[#3E4155]"
            }`}
          >
            <i className={`${c.riskLevel === "위험" ? "ri-error-warning-line text-[#E8375F]" : "ri-shield-check-line text-[#00C896]"} text-base`}></i>
            <span className="font-medium">{c.from.split(" ")[0]}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full border font-semibold ${
              c.riskLevel === "위험" ? "border-[#E8375F]/30 text-[#E8375F] bg-[#E8375F]/10" : "border-[#00C896]/30 text-[#00C896] bg-[#00C896]/10"
            }`}>{c.riskLevel}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Contract Text */}
        <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#252830]">
            <div className="text-[#111111] text-sm font-semibold">{selectedContract.title}</div>
            <div className="text-[#5A5E72] text-xs mt-0.5">발신: {selectedContract.from} · {selectedContract.received}</div>
          </div>
          <div className="p-4">
            <pre className="text-[#8B91A8] text-xs leading-relaxed whitespace-pre-wrap font-sans">{selectedContract.fullText}</pre>
            <button
              type="button"
              onClick={handleAnalyze}
              disabled={analysisState !== "idle"}
              className={`mt-4 w-full py-2.5 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
                analysisState === "idle"
                  ? "bg-gradient-to-r from-[#E8375F] to-[#FF4B7A] text-white cursor-pointer hover:shadow-lg"
                  : "bg-[#252830] text-[#5A5E72] cursor-not-allowed"
              }`}
            >
              {analysisState === "idle" && <><i className="ri-search-eye-line mr-2"></i>AI 독소조항 자동 분석</>}
              {analysisState === "analyzing" && <><i className="ri-loader-4-line animate-spin mr-2"></i>분석 중...</>}
              {analysisState === "done" && <><i className="ri-check-double-line mr-2"></i>분석 완료</>}
            </button>
          </div>
        </div>

        {/* Analysis Results */}
        <div className="space-y-3">
          {analysisState === "done" ? (
            <>
              <div className={`rounded-xl border p-4 ${
                selectedContract.toxicClauses.length > 0
                  ? "bg-[#E8375F]/10 border-[#E8375F]/30"
                  : "bg-[#00C896]/10 border-[#00C896]/30"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${selectedContract.toxicClauses.length > 0 ? "ri-error-warning-fill text-[#E8375F]" : "ri-shield-check-fill text-[#00C896]"} text-base`}></i>
                  </div>
                  <span className={`font-bold text-sm ${selectedContract.toxicClauses.length > 0 ? "text-[#E8375F]" : "text-[#00C896]"}`}>
                    {selectedContract.toxicClauses.length > 0
                      ? `독소조항 ${selectedContract.toxicClauses.length}개 발견됨`
                      : "이상 없음 - 승인 가능"}
                  </span>
                </div>
                <p className="text-[#8B91A8] text-xs">
                  {selectedContract.toxicClauses.length > 0
                    ? "아래 조항들은 귀사에 불리한 내용을 포함하고 있습니다. 수정 요청 메일을 발송하세요."
                    : "계약서의 모든 조항이 업계 표준 범위 내에 있습니다."}
                </p>
              </div>

              {selectedContract.toxicClauses.map((c, i) => (
                <div key={i} className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-4">
                  <div className="flex items-start gap-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 mt-0.5 ${getRiskStyle(c.severity)}`}>
                      {c.severity}
                    </span>
                    <div>
                      <div className="text-[#111111] text-xs font-semibold mb-1">{c.clause}</div>
                      <div className="text-[#8B91A8] text-xs leading-relaxed">{c.reason}</div>
                    </div>
                  </div>
                </div>
              ))}

              {selectedContract.toxicClauses.length > 0 && !replyMode && !replyDone && (
                <button
                  type="button"
                  onClick={() => { setReplyMode(true); setReplyText(`안녕하세요, ${selectedContract.from}님.\n\n보내주신 계약서를 검토한 결과 아래 조항들에 대한 수정을 요청드립니다.\n\n1. ${selectedContract.toxicClauses[0]?.clause}: ${selectedContract.toxicClauses[0]?.reason}\n\n상호 윈윈하는 계약 관계를 위해 조속한 수정 회신 부탁드립니다.\n\nK-ArtPEN 법무팀 드림`); }}
                  className="w-full py-2.5 bg-[#1C1F2E] border border-[#E8375F]/40 text-[#E8375F] text-sm font-semibold rounded-xl hover:bg-[#E8375F]/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line mr-2"></i>수정 요청 메일 작성
                </button>
              )}

              {replyMode && !replyDone && (
                <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-4 space-y-3">
                  <div className="text-[#111111] text-sm font-semibold">수정 요청 메일 작성</div>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={7}
                    className="w-full bg-[#252830] text-white text-xs px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] resize-none leading-relaxed"
                  />
                  <button type="button" onClick={handleReply} className="w-full py-2 bg-[#E8375F] text-white text-sm font-semibold rounded-lg hover:bg-[#FF4B7A] transition-colors cursor-pointer whitespace-nowrap">
                    발송
                  </button>
                </div>
              )}

              {replyDone && (
                <div className="bg-[#00C896]/10 border border-[#00C896]/30 rounded-xl p-4 text-[#00C896] text-sm font-semibold flex items-center gap-2">
                  <i className="ri-send-plane-fill text-lg"></i>수정 요청 메일이 발송되었습니다.
                </div>
              )}
            </>
          ) : (
            <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] h-48 flex items-center justify-center">
              <div className="text-center text-[#5A5E72]">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-search-eye-line text-3xl"></i>
                </div>
                <div className="text-sm">'AI 독소조항 자동 분석' 버튼을 클릭하면</div>
                <div className="text-sm">계약서 위험 조항이 자동으로 탐지됩니다.</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
